import { useLayoutEffect, useRef, useState } from "preact/hooks";
import { useFormEnv } from "../lib/context";
import { t } from "../lib/i18n";
import { Markdown } from "../lib/markdown";
import { BlockView } from "./BlockView";
import { Branding, DraftNotice, Duration, MailNotice, ResumeNotice, ResultsNotice, ResultsTeaser, SaveForLater, ErrorSummary, Honeypot, Logo, SubmitError, useFieldIds } from "./Common";
import { ReviewView } from "./ReviewView";

/** Abschnittsübersicht für lange Formulare (ab drei Seiten mit Titeln): erledigte Abschnitte sind anspringbar. */
function Sections() {
  const { ctl, tpl } = useFormEnv();
  const pages = ctl.flow.pages;
  if (pages.length < 3 || !pages.some((p) => ctl.idx.pageById.get(p.pageId)!.page.title)) return null;
  return (
    <nav class="fb-sections" aria-label={t.sections}>
      <ol>
        {pages.map((p, i) => {
          const title = ctl.idx.pageById.get(p.pageId)!.page.title;
          const label = `${i + 1}. ${title ? tpl(title) : t.sectionN(i + 1)}`;
          const done = ctl.reviewing || i < ctl.currentIndex;
          const current = !ctl.reviewing && i === ctl.currentIndex;
          return (
            <li key={p.pageId} class={`fb-section${done ? " is-done" : ""}${current ? " is-current" : ""}`}>
              {done && ctl.def.settings.allowBack ? (
                <button type="button" class="fb-section-btn" onClick={() => ctl.goToPage(p.pageId)}>
                  {label}
                  <span class="fb-sr"> – {t.sectionDone}</span>
                </button>
              ) : (
                <span aria-current={current ? "step" : undefined}>{label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function ClassicView() {
  const { ctl, tpl } = useFormEnv();
  const page = ctl.flow.pages.find((p) => p.pageId === ctl.current) ?? ctl.flow.pages[0]!;
  const def = ctl.idx.pageById.get(page.pageId)!.page;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const first = useRef(true);
  const [announce, setAnnounce] = useState("");
  const multi = ctl.total > 1;
  const fieldIds = useFieldIds(page.visibleBlockIds);

  useLayoutEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    headingRef.current?.focus();
    window.scrollTo?.({ top: 0 });
    setAnnounce(t.pageAnnounce(ctl.currentIndex + 1, ctl.total, def.title));
  }, [page.pageId]);

  const pageTitle = def.title ?? (multi ? t.progress(ctl.currentIndex + 1, ctl.total) : undefined);

  return (
    <div class="fb-shell fb-shell--classic">
      <header class="fb-header">
        <Logo />
        <h1 class="fb-title">{ctl.def.title}</h1>
        {ctl.currentIndex === 0 && !ctl.reviewing && <Duration />}
        {multi && ctl.def.settings.showProgress && (
          <div class="fb-steps" aria-hidden="true">
            <span class="fb-steps-text">
              {ctl.def.theme.progress === "percent" ? `${Math.round(((ctl.currentIndex + 1) / ctl.total) * 100)} %` : t.progress(ctl.currentIndex + 1, ctl.total)}
            </span>
            <span class="fb-steps-bar">
              <span style={{ width: `${((ctl.currentIndex + 1) / ctl.total) * 100}%` }} />
            </span>
          </div>
        )}
      </header>

      <Sections />
      <DraftNotice />
      <ResumeNotice />
      <MailNotice />

      {ctl.reviewing ? (
        <ReviewView />
      ) : (
      <form
        class="fb-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void ctl.next();
        }}
        aria-busy={ctl.status === "submitting" || ctl.waitingForTransfers}
      >
        <Honeypot onInput={ctl.setHoneypot} />
        <section class="fb-page" aria-labelledby={pageTitle ? "fb-page-title" : undefined}>
          {pageTitle && (
            <h2 id="fb-page-title" class="fb-page-title" tabIndex={-1} ref={headingRef}>
              {pageTitle}
            </h2>
          )}
          {def.description && (
            <div class="fb-page-desc">
              <Markdown text={tpl(def.description)} as="p" />
            </div>
          )}
          <ErrorSummary fieldIds={fieldIds} />
          {ctl.currentIndex === 0 && <ResultsTeaser />}
          <div class="fb-blocks">
            {page.visibleBlockIds.map((id) => (
              <BlockView key={id} block={ctl.idx.blockById.get(id)!.block} />
            ))}
          </div>
        </section>

        <SubmitError />
        <SaveForLater />
        {ctl.isLast && !ctl.def.settings.reviewBeforeSubmit && <ResultsNotice />}

        <div class="fb-nav">
          {ctl.def.settings.allowBack && ctl.currentIndex > 0 && (
            <button type="button" class="fb-btn fb-btn--ghost" onClick={ctl.back}>
              {t.back}
            </button>
          )}
          <button type="submit" class="fb-btn fb-btn--primary" disabled={ctl.status === "submitting" || ctl.waitingForTransfers}>
            {ctl.status === "submitting" ? t.submitting : ctl.waitingForTransfers ? t.transfersWaiting : ctl.isLast && !ctl.def.settings.reviewBeforeSubmit ? t.submit : t.next}
          </button>
        </div>
      </form>
      )}
      <p class="fb-sr" aria-live="polite">
        {announce}
      </p>
      <Branding />
    </div>
  );
}
